/**
 * Editor header: "Return for correction".
 *
 * Shown to QA-side users on a record that has actually been submitted (under review, partly
 * under review, or approved). It is deliberately offered on an APPROVED record too: that is
 * the only route back for a signed MBMR whose filter-integrity test turns out to have no
 * bubble-point limit, and without it a reviewer's only options are to say nothing or to
 * telephone the author.
 *
 * Client gating is `MBMR Approval` write access — the same hook every other page-level gate
 * in this product uses. The real refusal is the server's: `/bp/mbmr/return/` re-checks the
 * QA role, the separation of duties, the reason length and the e-signature and answers with
 * the rule that was broken, which this component shows verbatim rather than paraphrasing.
 */
import React, { useMemo, useState } from 'react';
import { Tooltip } from 'antd';
import { toast } from 'react-toastify';
import { Undo2 } from 'lucide-react';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import useModuleAccess from '@/hooks/useModuleAccess';
import { returnForCorrection } from '@/utils/mbmr_readiness_api';
import { useReadiness } from './ReadinessContext';
import ReturnForCorrectionModal from './ReturnForCorrectionModal';

export default function ReturnForCorrectionButton({ recipeId }) {
  const { isDark } = useTheme();
  const { model, lifecycle, blockers, applyLifecycle, reload } = useReadiness() || {};
  const access = useModuleAccess('Approvals');
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const mayReturn = Boolean(access?.create || access?.update || access?.isAdmin);
  const offered = Boolean(recipeId) && Boolean(lifecycle?.known) && lifecycle.return_available;

  // The rail's blocker rows, in the shape the modal's picker wants.
  const findings = useMemo(
    () =>
      (blockers || []).map((o) => ({
        operation_id: o.operation_id,
        operation_number: o.operation_number,
        operation_name: o.operation_name,
        blocking_reasons: o.blocking_reasons,
      })),
    [blockers]
  );

  // Nothing to return, or not a QA user: render nothing rather than a disabled control that
  // invites a click and then explains itself.
  if (!offered || !mayReturn) return null;

  const submit = async (payload) => {
    setSubmitting(true);
    setError(null);
    try {
      const body = await returnForCorrection({ recipe_id: recipeId, ...payload });
      applyLifecycle?.(body?.data?.lifecycle);
      reload?.();
      setOpen(false);
      toast.success(body?.message || 'Returned for correction');
    } catch (e) {
      setError(e);
    } finally {
      setSubmitting(false);
    }
  };

  const color = isDark ? defaultColors.errorLight : defaultColors.error;

  return (
    <>
      <Tooltip
        title={
          lifecycle.state === 'approved'
            ? 'This MBMR is approved. Returning it sends it back to its author as Review & Not Approved, with your reason and e-signature.'
            : 'Send this MBMR back to its author for correction, with a reason and your e-signature.'
        }
      >
        <button
          onClick={() => {
            setError(null);
            setOpen(true);
          }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-md bg-transparent shadow-sm transform-gpu transition-transform duration-200 hover:scale-105"
          style={{ color, border: `1px solid ${color}`, whiteSpace: 'nowrap' }}
        >
          <Undo2 className="w-3.5 h-3.5" />
          Return for correction
        </button>
      </Tooltip>
      <ReturnForCorrectionModal
        open={open}
        recipeName={model?.recipe_name || `Recipe ${recipeId}`}
        scopeLabel={model?.is_packaging ? 'MBPR' : 'MBMR'}
        findings={findings}
        submitting={submitting}
        error={error}
        onCancel={() => setOpen(false)}
        onSubmit={submit}
      />
    </>
  );
}
